"use client";
import React, { useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const QualifiedCandidates = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const isRoleLong = (role) => role.length > 80;

  const testimonials = [
    {
      text: t("QualifiedCandidatestestimonials.t1.text"),
      name: t("QualifiedCandidatestestimonials.t1.name"),
      role: t("QualifiedCandidatestestimonials.t1.role"),
      company: t("QualifiedCandidatestestimonials.t1.company"),
      avatar: IMAGES.HADEESA_AVATAR,
    },
    {
      text: t("QualifiedCandidatestestimonials.t2.text"),
      name: t("QualifiedCandidatestestimonials.t2.name"),
      role: t("QualifiedCandidatestestimonials.t2.role"),
      company: t("QualifiedCandidatestestimonials.t2.company"),
      avatar: IMAGES.MAHA_AVATAR,
    },
    {
      text: t("QualifiedCandidatestestimonials.t3.text"),
      name: t("QualifiedCandidatestestimonials.t3.name"),
      role: t("QualifiedCandidatestestimonials.t3.role"),
      company: t("QualifiedCandidatestestimonials.t3.company"),
      avatar: IMAGES.RAMIZ_AVATAR,
    },
    {
      text: t("QualifiedCandidatestestimonials.t4.text"),
      name: t("QualifiedCandidatestestimonials.t4.name"),
      role: t("QualifiedCandidatestestimonials.t4.role"),
      company: t("QualifiedCandidatestestimonials.t4.company"),
      avatar: IMAGES.MOAZZAM_AVATAR,
    },
    {
      text: t("QualifiedCandidatestestimonials.t5.text"),
      name: t("QualifiedCandidatestestimonials.t5.name"),
      role: t("QualifiedCandidatestestimonials.t5.role"),
      company: t("QualifiedCandidatestestimonials.t5.company"),
      avatar: IMAGES.SAJID_AVATAR,
    },
  ];

  const loopTestimonials = [...testimonials, ...testimonials];

  const TestimonialCard = ({ testimonial, isRoleLong }) => (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        height: { xs: "420px", sm: "450px" },
        position: "relative",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      {/* Inverted Comma */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 12, sm: 20 },
          left: isArabic ? "auto" : { xs: 16, sm: 25 },
          right: isArabic ? { xs: 16, sm: 25 } : "auto",
          width: { xs: "30px", sm: "40px" },
          height: { xs: "30px", sm: "40px" },
        }}
      >
        <Image
          src={IMAGES.INVERTED_COMMAS}
          alt="Quote"
          width={50}
          height={50}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Box>

      <Typography
        sx={{
          fontSize: { xs: "13px", sm: "15px" },
          fontWeight: "500",
          color: "black",
          lineHeight: 1.6,
          mt: { xs: 5, sm: 6 },
          mb: { xs: 8, sm: 9 },
          textAlign: isArabic ? "right" : "left",
          fontFamily: "var(--font-outfit)",
        }}
      >
        "{testimonial.text}"
      </Typography>

      {/* Author */}
      <Box
        sx={{
          position: "absolute",
          bottom: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          display: "flex",
          flexDirection: isArabic ? "row-reverse" : "row",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: { xs: "45px", sm: "55px" },
            height: { xs: "45px", sm: "55px" },
            borderRadius: "50%",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <Image
            src={testimonial.avatar}
            alt={testimonial.name}
            width={55}
            height={55}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "15px" },
              fontFamily: "var(--font-outfit)",
              fontWeight: "500",
              color: "black",
              mb: 0.3,
              textAlign: isArabic ? "right" : "left",
            }}
          >
            {testimonial.name}
          </Typography>
          <Typography
            {...(isRoleLong(testimonial.role) && {
              title: `${testimonial.role}, ${testimonial.company}`,
            })}
            sx={{
              fontSize: { xs: "11px", sm: "12px" },
              fontFamily: "var(--font-outfit)",
              fontWeight: "400",
              color: "#5e6065",
              lineHeight: 1.4,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              textAlign: isArabic ? "right" : "left",
              ...(isRoleLong(testimonial.role) && { cursor: "pointer" }),
            }}
          >
            {testimonial.role},{" "}
            <span style={{ color: "#5e6065" }}>{testimonial.company}</span>
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundImage: `url('${IMAGES.QUALIFIED_CANDIDATES_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        py: { xs: 2, sm: 4, md: 6 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      {/* Top Text Section */}
      <Box
        textAlign={{ xs: isArabic ? "right" : "left", md: "center" }}
        mb={8}
        mt={{ xs: 4, md: -2 }}
        px={{ xs: 2.5, sm: 3, md: 0 }}
      >
        <Chip
          label={t("QualifiedCandidatestestimonials.chip")}
          sx={{
            backgroundColor: "white",
            border: "1px solid #0206174D",
            mb: 3,
            color: "#818E98",
            fontSize: { xs: "11px", sm: "13px", md: "16px" },
            fontWeight: "500",
            textTransform: "capitalize",
            px: { xs: 1, sm: 0.7 },
            py: { xs: 1, sm: 1.5, md: 1 },
            fontFamily: "var(--font-outfit)",
            borderRadius: "40px",
            height: { xs: "auto", sm: "auto" },
          }}
        />

        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontWeight: "600",
            mb: { xs: 1.5, md: 1.3 },
            color: "#1e293b",
            fontSize: {
              xs: "1.5rem",
              sm: "1.875rem",
              md: "2.7rem",
              lg: "2.7rem",
              xl: "3.2rem",
            },
            lineHeight: { xs: 1.3, md: 1.2 },
            fontFamily: "var(--font-plusjakarta)",
            maxWidth: { xs: "100%", md: 1150 },
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            mx: { xs: 0, md: "auto" },
          }}
        >
          {t("QualifiedCandidatestestimonials.title1")}{" "}
          <span style={{ color: "#9048FB" }}>
            {t("QualifiedCandidatestestimonials.titleHighlight")}
          </span>{" "}
          {t("QualifiedCandidatestestimonials.title2")}
        </Typography>

        <Typography
          sx={{
            color: "#02061799",
            mb: 0.7,
            fontWeight: "400",
            fontSize: {
              xs: "14px",
              sm: "16px",
              md: "20px",
              lg: "20px",
              xl: "24px",
            },
            fontFamily: "var(--font-outfit)",
            mx: { xs: 0, md: "auto" },
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            maxWidth: { xs: "100%", md: 900 },
            mt: { xs: "3%", md: "2%" },
            lineHeight: { xs: 1.5, md: 1.3 },
          }}
        >
          {t("QualifiedCandidatestestimonials.subtitle")}
        </Typography>
      </Box>

      {/* MOBILE: Swiper */}
      <Box sx={{ display: { xs: "block", md: "none" }, px: 2 }}>
        <Swiper
          modules={[Autoplay]}
          spaceBetween={20}
          slidesPerView={1}
          loop={true}
          autoplay={{ delay: 2000, disableOnInteraction: false }}
          speed={800}
          dir={isArabic ? "rtl" : "ltr"}
          key={isArabic ? "rtl" : "ltr"}
        >
          {testimonials.map((testimonial, i) => (
            <SwiperSlide key={i}>
              <TestimonialCard
                testimonial={testimonial}
                isRoleLong={isRoleLong}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      {/* Desktop: Continuous Carousel — RTL Fixed */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          width: "200%",
          // ✅ FIX: Single animation for both LTR and RTL — no more RTL reverse scroll
          animation: "scrollTestimonials 40s linear infinite",
          "&:hover": { animationPlayState: "paused" },
          "@keyframes scrollTestimonials": {
            from: { transform: "translateX(0)" },
            to: { transform: "translateX(-50%)" },
          },
          // ✅ CRITICAL FIX: Always keep carousel wrapper as "ltr"
          // This ensures flex children stack left-to-right in both languages.
          // Previously "direction: rtl" was pushing all cards to the right side,
          // causing empty space on the left and cards going off-screen.
          // RTL text alignment is handled inside each individual card below.
          direction: "ltr",
        }}
      >
        {loopTestimonials.map((testimonial, index) => (
          <Box
            key={index}
            sx={{
              flex: {
                xs: "0 0 45%",
                sm: "0 0 45%",
                md: "0 0 30%",
                lg: "0 0 17%",
                xl: "0 0 14%",
              },
              px: { xs: 1.5, md: 2 },
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                bgcolor: "white",
                borderRadius: { xs: "12px", md: "16px" },
                p: { xs: 2, sm: 2.5, md: 4 },
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                height: {
                  xs: "400px",
                  sm: "480px",
                  md: "470px",
                  lg: "480px",
                  xl: "470px",
                },
                position: "relative",
                // ✅ RTL direction applied only inside each individual card
                // so Arabic text aligns right, avatar order flips, etc.
                direction: isArabic ? "rtl" : "ltr",
              }}
            >
              {/* Inverted Comma */}
              <Box
                sx={{
                  position: "absolute",
                  top: { xs: 12, sm: 15, md: 20 },
                  left: isArabic ? "auto" : { xs: 16, sm: 20, md: 30 },
                  right: isArabic ? { xs: 16, sm: 20, md: 30 } : "auto",
                  width: { xs: "30px", sm: "35px", md: "50px" },
                  height: { xs: "30px", sm: "35px", md: "50px" },
                }}
              >
                <Image
                  src={IMAGES.INVERTED_COMMAS}
                  alt="Quote"
                  width={50}
                  height={50}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: "13px", sm: "15px", md: "16px" },
                  fontWeight: "500",
                  color: "black",
                  lineHeight: { xs: 1.6, md: 1.7 },
                  mt: { xs: 5, sm: 6, md: 8 },
                  minHeight: { xs: "240px", sm: "260px", md: "280px" },
                  fontFamily: "var(--font-outfit)",
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                "{testimonial.text}"
              </Typography>

              {/* Author */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: { xs: 16, sm: 20, md: 25 },
                  left: { xs: 16, sm: 20, md: 32 },
                  right: { xs: 16, sm: 20, md: 32 },
                  display: "flex",
                  flexDirection: isArabic ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  gap: { xs: 1.5, md: 2 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: "40px", sm: "45px", md: "60px" },
                    height: { xs: "40px", sm: "45px", md: "60px" },
                    borderRadius: "50%",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <Image
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    width={60}
                    height={60}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "13px", sm: "15px", md: "18px" },
                      fontFamily: "var(--font-outfit)",
                      fontWeight: "500",
                      color: "black",
                      mb: 0.5,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {testimonial.name}
                  </Typography>
                  <Typography
                    {...(isRoleLong(testimonial.role) && {
                      title: `${testimonial.role} , ${testimonial.company}`,
                    })}
                    sx={{
                      fontSize: { xs: "11px", sm: "12px", md: "14px" },
                      fontFamily: "var(--font-outfit)",
                      fontWeight: "400",
                      color: "#5e6065",
                      lineHeight: 1.4,
                      height: { xs: "55px", sm: "60px", md: "65px" },
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      textAlign: isArabic ? "right" : "left",
                      ...(isRoleLong(testimonial.role) && {
                        cursor: "pointer",
                      }),
                    }}
                  >
                    {testimonial.role} ,{" "}
                    <span style={{ color: "#5e6065", fontWeight: "400" }}>
                      {testimonial.company}
                    </span>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default QualifiedCandidates;
